import React from 'react';

// One product tile in a public catalogue: image, selection checkbox, and
// either its footprint badge or a "request a footprint" affordance. Shared by
// the per-supplier catalogue grid and the cross-supplier search results, which
// differ only in whether the owning supplier needs naming on the card.

function parseImages(raw) {
  try { return Array.isArray(raw) ? raw : (typeof raw === 'string' ? JSON.parse(raw) : []); }
  catch { return []; }
}

// Sub-1 kg footprints get an extra decimal, so a 0.234 kg tote doesn't
// round away to 0.23 next to a 12.4 kg jacket.
export function formatCarbon(kg) {
  const n = Number(kg);
  if (!Number.isFinite(n)) return null;
  const decimals = n > 0 && n < 1 ? 3 : 2;
  return n.toFixed(decimals);
}

export default function CatalogueProductCard({
  product,
  selected = false,
  onToggle,
  onRequestPcf,
  showSupplier = false,
}) {
  const p = product;
  const heroImage = parseImages(p.images)[0];
  const isLinked = !!p.already_linked;
  const hasCheck = selected || isLinked;
  const carbon = formatCarbon(p.total_emissions_kg);

  return (
    <div
      onClick={() => onToggle && onToggle(p.id, isLinked)}
      style={{
        border: selected
          ? '2px solid #06a457'
          : isLinked
            ? '2px solid #b7e8cf'
            : '2px solid #e8f0eb',
        borderRadius: 6,
        overflow: 'hidden',
        background: '#ffffff',
        cursor: isLinked || !onToggle ? 'default' : 'pointer',
        transition: 'border-color 0.12s, box-shadow 0.12s',
        boxShadow: selected ? '0 0 0 3px rgba(6,164,87,0.12)' : 'none',
      }}
    >
      {/* Image area */}
      <div style={{ position: 'relative', paddingBottom: '75%', background: '#f5faf7' }}>
        {heroImage ? (
          <img
            src={heroImage}
            alt=""
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'repeating-linear-gradient(45deg, #e4ede8 0px, #e4ede8 1px, #f5faf7 1px, #f5faf7 14px)',
          }} />
        )}

        {onToggle && (
          <div style={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 22,
            height: 22,
            borderRadius: 5,
            background: hasCheck ? '#06a457' : 'rgba(255,255,255,0.92)',
            border: hasCheck ? 'none' : '1.5px solid #d1d5db',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            transition: 'background 0.12s',
          }}>
            {hasCheck && (
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                <path d="M1.5 5.5l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        )}

        {/* Supplier chip — only when the grid mixes suppliers, otherwise the
            page heading already says whose catalogue this is. */}
        {showSupplier && p.supplier_name && (
          <div style={{
            position: 'absolute',
            top: 8,
            left: 8,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            maxWidth: 'calc(100% - 46px)',
            padding: '4px 9px 4px 5px',
            background: 'rgba(255,255,255,0.94)',
            border: '1px solid #e8f0eb',
            borderRadius: 999,
            boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
            backdropFilter: 'blur(4px)',
          }}>
            {p.supplier_logo_url && (
              <img
                src={p.supplier_logo_url}
                alt=""
                style={{ width: 16, height: 16, borderRadius: 999, objectFit: 'contain', flexShrink: 0 }}
              />
            )}
            <span style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#052b16',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {p.supplier_name}
            </span>
          </div>
        )}

        {/* Request-a-footprint affordance, only where there's no footprint yet */}
        {!p.has_assessment && onRequestPcf && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (p.already_requested) return;
              onRequestPcf(p);
            }}
            disabled={p.already_requested}
            title={p.already_requested
              ? 'You have already requested a footprint for this product'
              : 'Ask the supplier to compute a footprint for this product'}
            style={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '5px 9px',
              fontSize: 11,
              fontWeight: 600,
              fontFamily: "'Raleway', sans-serif",
              letterSpacing: '0.03em',
              color: p.already_requested ? '#4b5563' : '#ffffff',
              background: p.already_requested ? 'rgba(229,231,235,0.95)' : '#ff5757',
              border: p.already_requested ? '1px solid #d1d5db' : '1px solid #ff5757',
              borderRadius: 999,
              cursor: p.already_requested ? 'default' : 'pointer',
              boxShadow: p.already_requested ? '0 1px 4px rgba(0,0,0,0.10)' : '0 2px 8px rgba(255,87,87,0.35)',
              transition: 'background 0.12s, border-color 0.12s, transform 0.12s',
              backdropFilter: 'blur(2px)',
            }}
          >
            {p.already_requested ? (
              <>
                <svg width="10" height="10" viewBox="0 0 11 11" fill="none">
                  <path d="M1.5 5.5l3 3 5-5" stroke="#4b5563" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Requested
              </>
            ) : (
              <>
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                  <path d="M6 1.5v9M1.5 6h9" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
                Request PCF
              </>
            )}
          </button>
        )}

        {/* Footprint badge */}
        {p.has_assessment && (
          <div
            title="Total product carbon footprint per unit"
            style={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 11px 5px 8px',
              fontFamily: "'Raleway', sans-serif",
              background: 'rgba(5,43,22,0.9)',
              borderRadius: 999,
              boxShadow: '0 4px 14px rgba(5,43,22,0.28)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, display: 'block' }}>
              <path
                d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"
                fill="#4ade80"
              />
              <path
                d="M2 21c0-3 1.85-5.36 5.08-6"
                stroke="#4ade80"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            {carbon ? (
              <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 3 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#ffffff', letterSpacing: '-0.01em', lineHeight: 1 }}>
                  {carbon}
                </span>
                <span style={{
                  fontSize: 9.5,
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.7)',
                  letterSpacing: '0.02em',
                  lineHeight: 1,
                }}>
                  kg CO₂e
                </span>
              </span>
            ) : (
              <span style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#ffffff',
              }}>
                PCF
              </span>
            )}
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: '10px 12px' }}>
        <div style={{
          fontWeight: 600,
          fontSize: 13,
          color: '#052b16',
          marginBottom: 5,
          lineHeight: 1.3,
        }}>
          {p.name}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>{p.code || '—'}</span>
          {!p.has_assessment && (
            <span style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
              No PCF
            </span>
          )}
        </div>
        {isLinked && (
          <div style={{ marginTop: 5, fontSize: 11, color: '#06a457', fontWeight: 500 }}>
            In your catalogue
          </div>
        )}
      </div>
    </div>
  );
}
